import type { BaseCanvasElement } from '@/typings/customilyElements/canvasElements';
import type { ElementType, ValueFormat } from '@/typings/designStudio/sourceElementTypes';
import type { CanvasEx } from '@/typings/canvas';

/** Per element type operations the pattern handlers delegate to (texts: TextService, images: imagePatternReplicas) */
export interface PatternReplicaService {
    elementType: ElementType;
    /** Replicas built/synced per batch, yielding to the browser between them. Omitted: all at once (texts) */
    batchSize?: number;
    /** With batchSize: yield only once this much work piled up since the last yield. Omitted: after every batch */
    batchTimeBudgetMs?: number;
    buildPatternReplica: (
        canvas: CanvasEx,
        origin: PatternElement,
        cell: PatternCell,
        options: { uuid?: string; left: number; top: number; zIndex?: number; editMode?: boolean }
    ) => PatternElement | Promise<PatternElement>;
    repositionPatternReplica: (
        origin: PatternElement,
        replica: PatternElement,
        position: { left: number; top: number },
        resize: boolean
    ) => void;
    syncPatternReplica: (
        canvas: CanvasEx,
        origin: PatternElement,
        replica: PatternElement,
        position: { left: number; top: number }
    ) => Promise<void>;
}

/** An elementsLinks entry as ProductService keeps it; pattern ones link an origin (source) to a replica (target) */
export interface PatternLink {
    sourceId: string;
    targetId: string;
    targetType?: ElementType;
    valueFormat: ValueFormat;
}

export interface PatternProductService {
    addPatternElementsLink: (originUuid: string, replicaUuid: string, elementType: ElementType) => void;
    removePatternElementsLinks: (originUuid: string) => void;
    removePatternElementsLinkByReplica: (replicaUuid: string) => void;
    getPatternReplicaLinks: (originUuid: string) => PatternLink[];
}

export interface CreatePatternHandlersParams {
    getCanvas: () => CanvasEx;
    $scope: { editMode?: boolean };
    ProductService: PatternProductService;
    getReplicaService: (origin: PatternElement) => PatternReplicaService;
}

export interface PatternProps {
    pattern?: boolean;
    patternConfig?: PatternConfig | null;
    isPatternReplica?: boolean;
    patternOriginUuid?: string;
    patternCell?: PatternCell;
}

/** Only what texts and image placeholders share: the per type replica services cast to their own element */
export type PatternElement = BaseCanvasElement & PatternProps;

export interface PatternConfig {
    spacingX: number;
    spacingY: number;
    offsetX: number;
    offsetY: number;
    rows: number;
    cols: number;
}

export const PATTERN_DEFAULTS: PatternConfig = {
    spacingX: 0,
    spacingY: 0,
    offsetX: 0,
    offsetY: 0,
    rows: 1,
    cols: 1
};

export interface PatternCell {
    row: number;
    col: number;
}

export interface CellPosition extends PatternCell {
    left: number;
    top: number;
}
